import {
  BookOpenCheck,
  CalendarCheck,
  Languages,
  type LucideIcon,
  MessageSquareText,
  PhoneForwarded,
  ScrollText,
} from "lucide-react"

import { Eyebrow } from "./eyebrow"

interface Feature {
  icon: LucideIcon
  title: string
  description: string
}

/**
 * The "included with every job" strip: platform guarantees and shared
 * conversational skills. This section exists so the job blocks above never
 * repeat these — jobs differentiate by mission and channel, and the shared
 * skills live here once. It also answers "why pay per job when they all
 * book?": you pay per job covered, the skills come with the employee.
 */
const FEATURES: Feature[] = [
  {
    icon: BookOpenCheck,
    title: "Trained on your business",
    description:
      "Knows your hours, services, and other details so callers get accurate answers.",
  },
  {
    icon: CalendarCheck,
    title: "Books your calendar",
    description:
      "Appointments go straight into your calendar, with no back-and-forth.",
  },
  {
    icon: PhoneForwarded,
    title: "Transfers live calls",
    description:
      "Routes callers to the right person on your team, the moment they need one.",
  },
  {
    icon: MessageSquareText,
    title: "Takes messages",
    description:
      "Every conversation summarised and sent to you, so nothing waits on a callback.",
  },
  {
    icon: Languages,
    title: "Multi-lingual",
    description:
      "Answers fluently in English, Spanish, and a range of other languages.",
  },
  {
    icon: ScrollText,
    title: "Everything on record",
    description:
      "Transcripts and recordings for every conversation, there when you need them.",
  },
]

export function Features() {
  return (
    <section id="features" className="w-full px-4 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto w-full max-w-5xl">
        <div className="flex flex-col items-center text-center">
          <Eyebrow>Features</Eyebrow>
          <h2 className="font-lp-serif text-lp-fg mt-3 text-[clamp(1.625rem,2.8vw,2.125rem)] leading-[1.1] tracking-[-0.02em]">
            Included with every job
          </h2>
          <p className="text-lp-muted mt-4 max-w-[46ch] text-base leading-[1.65]">
            The skills come with the employee. You just choose what it covers.
          </p>
        </div>

        {/* gap-px over a hairline background draws the internal grid lines. */}
        <div className="bg-lp-border/70 mt-16 grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="bg-lp-bg flex flex-col items-center px-8 py-12 text-center sm:py-14"
            >
              <span className="lp-tile grid size-14 place-items-center rounded-2xl">
                <feature.icon className="size-5" />
              </span>
              <h3 className="text-lp-fg mt-6 text-base font-semibold">
                {feature.title}
              </h3>
              <p className="text-lp-muted mt-2 max-w-[34ch] text-sm leading-[1.65]">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
